import { useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Job, Task, TaskStatus } from './types';
import { GET_TASKS } from './queries';
import { useTaskManagerContext } from './TaskManagerContext';
import useV2Query from 'utils/useV2Query';
import { useQueryParamsWithDefaults } from './useQueryParamsWithDefaults';
import { handleBackwardsCompatibilityWithCommaSeparatedJobTypes } from './task-filters/utils';

const PAGINATION_LIMIT = 150;
const defaultPagination = {
  limit: PAGINATION_LIMIT,
};

const defaultFilterBy = {
  clientIds: undefined,
  bookkeeperIds: undefined,
  taskIds: undefined,
  taskTypes: undefined,
  jobTypes: undefined,
  assignedPersonId: undefined,
  status: TaskStatus.PENDING_WORK,
};

type PaginationVariables = {
  startingAfter?: string;
  endingBefore?: string;
  limit: number;
};

type UsePaginatedTasksReturn = {
  getNextPage: (() => void) | null;
  getPrevPage: (() => void) | null;
  total: number;
  tasks?: Task[];
  loading: boolean;
  error?: ApolloError;
};

type UsePaginatedTasksProps = {
  pagination?: PaginationVariables;
  isBpaView?: boolean;
  requireTeam?: boolean;
};

type UsePaginatedTasks = (options: UsePaginatedTasksProps) => UsePaginatedTasksReturn;

type TaskWithJobDueDate = Task & { job: Job & { dueDate: string } };
type TaskWithJobPriority = Task & { job: Job & { priority: number } };

const usePaginatedTasks: UsePaginatedTasks = ({
  pagination = defaultPagination,
  isBpaView = false,
  requireTeam = false,
}) => {
  const { query } = useQueryParamsWithDefaults(isBpaView, requireTeam);
  const { sessionAssignedTasks, clearSessionAssignedTasks } = useTaskManagerContext();
  const [previousQuery, setPreviousQuery] = useState<typeof query>(query);
  const [paginationState, setPagination] = useState<PaginationVariables>({
    ...defaultPagination,
    ...pagination,
  });
  const { assignedPersonId, jobTypes, team, status } = query;

  const { data, loading, error } = useV2Query(GET_TASKS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip:
      (requireTeam && !team && !jobTypes?.length) ||
      (isBpaView && !assignedPersonId) ||
      (!requireTeam && !isBpaView && !query.clientIds?.length),
    variables: {
      filterBy: {
        ...query,
        jobTypes: jobTypes?.length
          ? handleBackwardsCompatibilityWithCommaSeparatedJobTypes(jobTypes)
          : undefined,
        assignedPersonId: assignedPersonId !== undefined ? assignedPersonId : undefined,
        status: status !== TaskStatus.ALL ? status : undefined,
        team: undefined,
      },
      pagination: paginationState,
    },
    onCompleted: () => {
      if (JSON.stringify(previousQuery) !== JSON.stringify(query)) {
        clearSessionAssignedTasks?.();
        setPreviousQuery(query);
      }
    },
  });

  const hasSessionAssignedTasks = (): boolean => {
    return data?.tasks.tasks && sessionAssignedTasks?.length;
  };

  const hasDueDate = (task: Task): task is TaskWithJobDueDate => {
    return 'dueDate' in task.job;
  };

  const hasPriority = (task: Task): task is TaskWithJobPriority => {
    return 'priority' in task.job;
  };

  const getTaskPage = () => {
    if (hasSessionAssignedTasks()) {
      return [
        ...(sessionAssignedTasks ?? []),
        ...data.tasks.tasks.filter(
          (task: Task) => !(sessionAssignedTasks ?? []).some(t => t.id === task.id),
        ),
      ].sort((a: Task, b: Task) => {
        const sortDueDate = hasDueDate(a) && hasDueDate(b);
        const sortPriority = hasPriority(a) && hasPriority(b);
        return (
          (sortDueDate && new Date(a.job.dueDate).getTime() - new Date(b.job.dueDate).getTime()) ||
          (sortPriority && a.job.priority - b.job.priority) ||
          a.id - b.id
        );
      });
    }
    return data?.tasks.tasks;
  };

  const pageInfo = data?.tasks.pageInfo;

  const getNextPage = pageInfo?.hasNextPage
    ? () =>
        setPagination({
          ...paginationState,
          startingAfter: pageInfo.endCursor,
          endingBefore: undefined,
        })
    : null;
  const getPrevPage = pageInfo?.hasPrevPage
    ? () =>
        setPagination({
          ...paginationState,
          endingBefore: pageInfo.startCursor,
          startingAfter: undefined,
        })
    : null;

  return {
    getNextPage,
    getPrevPage,
    total: pageInfo?.total,
    tasks: getTaskPage(),
    loading,
    error,
  };
};

export { usePaginatedTasks, defaultFilterBy, defaultPagination };
